
import { Component, Vue, Prop } from "vue-property-decorator"

import { InFieldConfiguration, IntegrationColumn } from "@/types/integration"
import { CaseColumn } from "@/types/caseColumns"

import vSelect from "vue-select"
import DeleteButton from "@/components/New/Page/PageComponents/DeleteButton.vue"
import { BButton } from "bootstrap-vue"
import { FieldTypeId } from "@/types/fieldTypes"

@Component({
	components: {
		"v-select": vSelect,
		"delete-btn": DeleteButton
	}
})
export default class FieldMappingRow extends Vue {
	@Prop({ required: true })
	field!: IntegrationColumn

	@Prop({ required: true })
	caseFields!: CaseColumn[]

	@Prop({ type: Array, default: () => [] })
	allFields!: IntegrationColumn[]

	@Prop({ type: Array, default: () => [] })
	mappedKeys!: string[]

	@Prop({ type: Boolean, default: false })
	allowEdit!: boolean

	@Prop({ type: Boolean, default: false })
	allowCreateFields!: boolean

	@Prop({ type: Boolean, default: true })
	allowDeleteMapping!: boolean

	@Prop({ type: Boolean, default: true })
	allowFieldSelection!: boolean

	@Prop({ type: Boolean, default: false })
	allowSpecialCharactersInKey!: boolean

	@Prop({ default: {} })
	inFieldConfiguration!: { [key: string]: InFieldConfiguration }

	@Prop()
	value!: string | null

	@Prop()
	renaming!: boolean

	@Prop({ type: Boolean, default: false })
	requirePrimaryKey!: boolean

	@Prop({ type: Boolean, default: false })
	isPrimaryKey!: boolean

	feedback: string[] = []

	private get primaryKeyTooltip (): string {
		if (!this.allowedAsPrimaryKey) {
			return "Only text and number fields can be set as a Primary Key"
		}
		return this.isPrimaryKey
			? "This field is selected as the Primary Key"
			: "Click to set this field as the Primary Key"
	}

	private get displaySelectedValue (): string {
		if (!this.value) return ""

		const selectedField = this.caseFields.find(f => f.key === this.value)
		return selectedField ? selectedField.label : this.value
	}

	get isRenaming (): boolean {
		return this.renaming
	}
	set isRenaming (value: boolean) {
		this.$emit("update:renaming", value)
	}

	async setName (newKey: string) {
		if (!this.field) {
			return
		}

		if (newKey === this.field.key) {
			this.isRenaming = false
			return
		} else if (!this.newKeyIsValid(newKey)) {
			return
		}
		this.$emit("setKey", newKey)
		this.isRenaming = false
	}

	private onSetNameBlur (newKey: string) {
		setTimeout(() => {
			if (this.isRenaming) {
				this.setName(newKey)
			}
		}, 100)
	}

	newKeyIsValid (key: string): boolean {
		if (!this.field) {
			return false
		}

		if (key === this.field.key) {
			// Not changed
			this.feedback = []
			return true
		}

		let feedback: string[] = []
		if (key.length === 0) {
			feedback.push("Key cannot be blank")
		} else if (this.allFields.find(f => f.key === key) || this.mappedKeys.find(k => k === key)) {
			feedback.push("Key already exists")
		} else if (key.startsWith("$")) {
			feedback.push("Key cannot start with '$'")
		} else if (!this.allowSpecialCharactersInKey && !key.match(/^[A-Za-z0-9$-]+$/)) {
			feedback.push("Key can only contain letters, numbers, and dashes -")
		}

		if (feedback.length === 0) {
			this.feedback = []
			return true
		} else {
			this.feedback = feedback
			return false
		}
	}

	/**
	 * Enables or Disables Rich Text for the relevant field mapping
	 * @param fieldKey of the mapping
	 * @param enabled rich text enabled/disabled
	 */
	setRichTextforField (fieldKey: string, enabled: boolean) {
		let fieldConfig = this.inFieldConfiguration ? this.inFieldConfiguration[fieldKey] : null

		// Set field Config if undefined
		if (!fieldConfig) {
			fieldConfig = {
				useRichText: enabled
			}
		} else {
			fieldConfig.useRichText = enabled
		}

		this.$emit("inFieldConfiguration", { fieldKey, fieldConfig })
	}

	cancelRenaming () {
		this.feedback = []
		this.isRenaming = false
	}

	fieldValueSet (newValue: string | null) {
		this.$emit("input", newValue)
	}

	createNewField () {
		this.$emit("createNewField", this.field)
	}

	deleteField () {
		this.$emit("delete", this.field)
	}

	private setPrimaryKey (isChecked: boolean) {
		this.$emit("setPrimaryKey", isChecked ? this.value : null)
	}

	private get allowedAsPrimaryKey (): boolean {
		const allowedPrimaryKeyDataTypes = [
			FieldTypeId.Text,
			FieldTypeId.TextArea,
			FieldTypeId.Number,
			FieldTypeId.Dollar
		]
		return allowedPrimaryKeyDataTypes.includes(this.field.dataType as FieldTypeId)
	}
}

